import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Encounter, LabRequest, RadiologyRequest } from '../../types';
import { Search, FileText, FlaskConical, Scan, Printer, Download, X } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
import jsPDF from 'jspdf';
interface Props {}
export const PatientReports: React.FC<Props> = () => {
  const {
    encounters,
    patients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewing, setViewing] = useState<{
    encounter: Encounter;
  } | null>(null);
  // Encounters that have at least one completed lab OR a radiology report
  const reportEncounters = useMemo(() => {
    return encounters.filter(e => {
      const hasCompletedLab = e.labRequests.some(l => l.status === 'Completed');
      const hasRadiologyReport = e.radiologyRequests.some(r => r.report || r.images && r.images.length > 0);
      return hasCompletedLab || hasRadiologyReport;
    }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [encounters]);
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reportEncounters;
    return reportEncounters.filter(e => {
      const p = patients.find(pt => pt.id === e.patientId);
      if (!p) return false;
      return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
    });
  }, [reportEncounters, patients, searchQuery]);
  const getPatient = (id: string) => patients.find(p => p.id === id);
  const handlePrint = () => window.print();
  const handleDownloadPDF = () => {
    if (!viewing) return;
    const patient = getPatient(viewing.encounter.patientId);
    if (!patient) return;
    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a4'
    });
    const margin = 40;
    let y = margin;
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Patient Report', margin, y);
    y += 22;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${patient.firstName} ${patient.lastName} · BMC ${patient.bmcFileNumber} · ${patient.age} yrs · ${patient.sex}`, margin, y);
    y += 14;
    pdf.text(`Visit date: ${new Date(viewing.encounter.startedAt).toLocaleString()}`, margin, y);
    y += 20;
    const lineCheck = () => {
      const ph = pdf.internal.pageSize.getHeight() - margin;
      if (y > ph) {
        pdf.addPage();
        y = margin;
      }
    };
    // ── Laboratory results ──
    const completedLabs = viewing.encounter.labRequests.filter(l => l.status === 'Completed');
    if (completedLabs.length > 0) {
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Laboratory Results', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      completedLabs.forEach(lab => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
        pdf.text(`Tests: ${activeTests.join(', ')}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (lab.completedAt) {
          pdf.text(`Completed: ${new Date(lab.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        if (lab.results) {
          Object.entries(lab.results).forEach(([k, v]) => {
            if (!v) return;
            lineCheck();
            const line = `  ${k}: ${v}`;
            const lines = pdf.splitTextToSize(line, 515);
            pdf.text(lines, margin, y);
            y += lines.length * 11;
          });
        }
        if (lab.otherTests) {
          lineCheck();
          pdf.text(`  Other: ${lab.otherTests}`, margin, y);
          y += 12;
        }
        if (lab.labComments) {
          lineCheck();
          pdf.setFont('helvetica', 'bold');
          pdf.text('Comments / Observations:', margin, y);
          y += 12;
          pdf.setFont('helvetica', 'normal');
          const lines = pdf.splitTextToSize(lab.labComments, 515);
          lines.forEach((line: string) => {
            lineCheck();
            pdf.text(line, margin, y);
            y += 11;
          });
        }
        y += 6;
      });
    }
    // ── Radiology results ──
    const radWithReport = viewing.encounter.radiologyRequests.filter(r => r.report);
    if (radWithReport.length > 0) {
      lineCheck();
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Radiology Reports', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      radWithReport.forEach(r => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Scan: ${r.scanType}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (r.completedAt) {
          pdf.text(`Completed: ${new Date(r.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        const lines = pdf.splitTextToSize(r.report || '', 515);
        lines.forEach((line: string) => {
          lineCheck();
          pdf.text(line, margin, y);
          y += 11;
        });
        y += 8;
      });
    }
    pdf.save(`report-${patient.bmcFileNumber}-${new Date(viewing.encounter.startedAt).toISOString().slice(0, 10)}.pdf`);
  };
  // ── Renderers for the View modal ──
  const renderLabResults = (lab: LabRequest) => {
    if (!lab.results) return null;
    const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
    const focusedTest = activeTests[0];
    if (isFBCTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-4757">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-4758">
            FBC Report
          </h4>
          {fbcStructure.map(cat => <div key={cat.category} className="mb-3" data-id="element-4759">
              <p className="font-semibold text-gray-700 mb-1" data-id="element-4760">{cat.category}</p>
              <table className="w-full text-xs border border-gray-300" data-id="element-4761">
                <thead className="bg-gray-100" data-id="element-4762">
                  <tr data-id="element-4763">
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-4764">
                      Test
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-24" data-id="element-4765">
                      Result
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-4766">
                      Reference
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-20" data-id="element-4767">
                      Unit
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-4768">
                  {cat.tests.map(t => <tr key={t.name} data-id="element-4769">
                      <td className="border border-gray-300 p-1.5" data-id="element-4770">{t.name}</td>
                      <td className="border border-gray-300 p-1.5 font-bold" data-id="element-4771">
                        {lab.results![t.name] || '—'}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4772">
                        {t.range}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4773">
                        {t.unit}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>)}
        </div>;
    }
    if (isUrinalysisTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-4774">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-4775">
            Urinalysis Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-4776">
            <thead className="bg-gray-100" data-id="element-4777">
              <tr data-id="element-4778">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-4779">
                  Parameter
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-32" data-id="element-4780">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-4781">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4782">
              {urinalysisTests.map(t => <tr key={t.name} data-id="element-4783">
                  <td className="border border-gray-300 p-1.5" data-id="element-4784">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-4785">
                    {lab.results![`UA_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4786">
                    {t.normal || '—'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isTestosteroneTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-4787">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-4788">
            Testosterone Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-4789">
            <thead className="bg-gray-100" data-id="element-4790">
              <tr data-id="element-4791">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-4792">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-4793">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-4794">
                  Reference (M)
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-4795">
                  Reference (F)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-4796">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4797">
              {testosteroneTests.map(t => <tr key={t.name} data-id="element-4798">
                  <td className="border border-gray-300 p-1.5" data-id="element-4799">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-4800">
                    {lab.results![`TESTO_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4801">
                    {t.rangeMale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4802">
                    {t.rangeFemale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4803">
                    {t.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isAMHTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-4804">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-4805">
            Anti Mullerian Hormone (AMH) Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-4806">
            <thead className="bg-gray-100" data-id="element-4807">
              <tr data-id="element-4808">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-4809">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-4810">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-4811">
                  Reference (by age)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-4812">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4813">
              <tr className="align-top" data-id="element-4814">
                <td className="border border-gray-300 p-1.5 font-medium" data-id="element-4815">
                  {amhStructure.name}
                </td>
                <td className="border border-gray-300 p-1.5 font-bold" data-id="element-4816">
                  {lab.results![amhStructure.storageKey] || focusedTest && lab.results![focusedTest] || lab.results!['AMH'] || lab.results!['Anti-Müllerian Hormone'] || lab.results!['ANTI-MÜLLERIAN HORMONE'] || lab.results!['Anti Mullerian Hormone (AMH)'] || '—'}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4817">
                  {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-4818">
                      <span className="font-medium" data-id="element-4819">{a.ageBand}:</span>{' '}
                      {a.range}
                    </div>)}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4820">
                  {amhStructure.unit}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[10px] italic text-gray-600" data-id="element-4821">
            Method: {amhStructure.method}
          </p>
        </div>;
    }
    if (isHormonalProfileTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-4822">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-4823">
            Hormonal Profile Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-4824">
            <thead className="bg-gray-100" data-id="element-4825">
              <tr data-id="element-4826">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-4827">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-4828">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-4829">
                  Reference Range
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-4830">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4831">
              {hormonalProfileTests.map(h => <tr key={h.key} data-id="element-4832">
                  <td className="border border-gray-300 p-1.5 font-medium" data-id="element-4833">
                    {h.name}
                  </td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-4834">
                    {lab.results![`HP_${h.key}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4835">
                    {h.ranges.map(r => <div key={r.label} data-id="element-4836">
                        <span className="font-medium" data-id="element-4837">{r.label}:</span>{' '}
                        {r.value}
                      </div>)}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-4838">
                    {h.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    // Fallback: generic key/value table
    return <div className="text-sm" data-id="element-4839">
        <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-4840">
          {activeTests.join(', ')} Report
        </h4>
        <table className="w-full text-xs border border-gray-300" data-id="element-4841">
          <thead className="bg-gray-100" data-id="element-4842">
            <tr data-id="element-4843">
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-4844">
                Parameter
              </th>
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-4845">Result</th>
            </tr>
          </thead>
          <tbody data-id="element-4846">
            {Object.entries(lab.results).map(([k, v]) => v ? <tr key={k} data-id="element-4847">
                  <td className="border border-gray-300 p-1.5" data-id="element-4848">{k}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-4849">
                    {v}
                  </td>
                </tr> : null)}
          </tbody>
        </table>
      </div>;
  };
  const renderRadReport = (r: RadiologyRequest) => <div className="text-sm" data-id="element-4850">
      <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-4851">
        Radiology Report — {r.scanType}
      </h4>
      {r.completedAt && <p className="text-xs text-gray-500 mb-2" data-id="element-4852">
          Completed: {new Date(r.completedAt).toLocaleString()}
        </p>}
      <div className="bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap text-sm" data-id="element-4853">
        {r.report || 'No report available.'}
      </div>
      {r.images && r.images.length > 0 && <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-4854">
          {r.images.map((img, idx) => <img key={idx} src={img} alt={`${r.scanType} ${idx + 1}`} className="rounded border border-gray-200 object-cover w-full max-h-64" data-id="element-4855" />)}
        </div>}
    </div>;
  return <>
      <Card data-id="element-4856">
        <div className="flex justify-between items-center mb-4 print:hidden" data-id="element-4857">
          <div className="flex items-center gap-2 flex-1 max-w-md" data-id="element-4858">
            <div className="relative w-full" data-id="element-4859">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4860">
                <Search className="h-4 w-4 text-gray-400" data-id="element-4861" />
              </div>
              <input type="text" placeholder="Search by patient name or BMC..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-4862" />
            </div>
          </div>
          <Badge variant="info" className="text-sm" data-id="element-4863">
            {filtered.length} report{filtered.length === 1 ? '' : 's'}
          </Badge>
        </div>

        {filtered.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-4864">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-4865" />
            <p className="text-lg font-medium text-gray-900" data-id="element-4866">No reports yet</p>
            <p className="text-sm" data-id="element-4867">
              Completed lab and radiology reports will appear here.
            </p>
          </div> : <ul className="divide-y divide-gray-200" data-id="element-4868">
            {filtered.map(enc => {
          const patient = getPatient(enc.patientId);
          if (!patient) return null;
          const completedLabs = enc.labRequests.filter(l => l.status === 'Completed');
          const completedRads = enc.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0);
          return <li key={enc.id} className="py-4 flex justify-between items-start gap-4" data-id="element-4869">
                  <div className="flex-1" data-id="element-4870">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-4871">
                      <p className="text-base font-bold text-gray-900" data-id="element-4872">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <Badge variant="info" data-id="element-4873">BMC: {patient.bmcFileNumber}</Badge>
                      <span className="text-xs text-gray-500" data-id="element-4874">
                        {patient.age} yrs · {patient.sex}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-4875">
                      Visit: {new Date(enc.startedAt).toLocaleString()}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-4876">
                      {completedLabs.length > 0 && <span className="inline-flex items-center text-xs bg-yellow-50 text-yellow-800 px-2 py-1 rounded border border-yellow-200" data-id="element-4877">
                          <FlaskConical className="w-3 h-3 mr-1" data-id="element-4878" />
                          {completedLabs.length} Lab Report
                          {completedLabs.length > 1 ? 's' : ''}
                        </span>}
                      {completedRads.length > 0 && <span className="inline-flex items-center text-xs bg-purple-50 text-purple-800 px-2 py-1 rounded border border-purple-200" data-id="element-4879">
                          <Scan className="w-3 h-3 mr-1" data-id="element-4880" />
                          {completedRads.length} Radiology Report
                          {completedRads.length > 1 ? 's' : ''}
                        </span>}
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => setViewing({
              encounter: enc
            })} data-id="element-4881">
                    <FileText className="w-4 h-4 mr-2" data-id="element-4882" /> View Report
                  </Button>
                </li>;
        })}
          </ul>}
      </Card>

      {viewing && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-4883">
          <div className="min-h-full flex items-start justify-center p-4" data-id="element-4884">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-4885">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white z-10 print:hidden" data-id="element-4886">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-4887">
                  Patient Report
                </h3>
                <div className="flex items-center gap-2" data-id="element-4888">
                  <Button variant="outline" onClick={handlePrint} data-id="element-4889">
                    <Printer className="w-4 h-4 mr-2" data-id="element-4890" /> Print
                  </Button>
                  <Button variant="outline" onClick={handleDownloadPDF} data-id="element-4891">
                    <Download className="w-4 h-4 mr-2" data-id="element-4892" /> Download PDF
                  </Button>
                  <button onClick={() => setViewing(null)} data-id="element-4893">
                    <X className="w-5 h-5 text-gray-400" data-id="element-4894" />
                  </button>
                </div>
              </div>
              <div id="report-printable" className="p-6 overflow-y-auto flex-1" data-id="element-4895">
                {(() => {
              const patient = getPatient(viewing.encounter.patientId);
              if (!patient) return null;
              return <>
                      <div className="border-b-2 border-black pb-3 mb-4" data-id="element-4896">
                        <h2 className="text-xl font-bold uppercase" data-id="element-4897">
                          Patient Report
                        </h2>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm" data-id="element-4898">
                          <p data-id="element-4899">
                            <span className="font-semibold" data-id="element-4900">Patient:</span>{' '}
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p data-id="element-4901">
                            <span className="font-semibold" data-id="element-4902">BMC #:</span>{' '}
                            {patient.bmcFileNumber}
                          </p>
                          <p data-id="element-4903">
                            <span className="font-semibold" data-id="element-4904">Age:</span>{' '}
                            {patient.age} yrs
                          </p>
                          <p data-id="element-4905">
                            <span className="font-semibold" data-id="element-4906">Sex:</span>{' '}
                            {patient.sex}
                          </p>
                          <p className="col-span-2" data-id="element-4907">
                            <span className="font-semibold" data-id="element-4908">Visit date:</span>{' '}
                            {new Date(viewing.encounter.startedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').map(lab => <div key={lab.id} className="mb-6" data-id="element-4909">
                            {renderLabResults(lab)}
                            {lab.labComments && <div className="mt-4 border border-gray-300 bg-gray-50 rounded p-3" data-id="element-4910">
                                <h5 className="font-bold uppercase mb-1 text-xs" data-id="element-4911">
                                  Comments / Observations
                                </h5>
                                <pre className="whitespace-pre-wrap font-sans text-xs text-gray-800" data-id="element-4912">
                                  {lab.labComments}
                                </pre>
                              </div>}
                          </div>)}

                      {viewing.encounter.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0).map(r => <div key={r.id} className="mb-6" data-id="element-4913">
                            {renderRadReport(r)}
                          </div>)}

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').length === 0 && viewing.encounter.radiologyRequests.filter(r => r.report).length === 0 && <p className="text-gray-500 text-center py-8" data-id="element-4914">
                            No reports available for this encounter.
                          </p>}
                    </>;
            })()}
              </div>
            </div>
          </div>
        </div>}

      {/* Print-specific styling */}
      <style data-id="element-4915">{`
        @media print {
          body * { visibility: hidden; }
          #report-printable, #report-printable * { visibility: visible; }
          #report-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
        }
      `}</style>
    </>;
};